import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Container } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Heart, Receipt, UserPlus, Crown } from "lucide-react";
import InstallNudge from "@/components/InstallNudge";
import PushToggle from "@/components/PushToggle";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 12;

export default function DonationConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user } = useAuth();
  const [state, setState] = useState("polling"); // polling | paid | pending | error
  const [info, setInfo] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const pollCount = useRef(0);
  const followedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (data.donation_id) {
            api.get(`/receipts/donation/${data.donation_id}`).then((r) => setReceipt(r.data)).catch(() => {});
          }
          return;
        }
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) { setState("error"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  // Signed-in donors: auto-follow the campaign so they get updates + can enable push.
  useEffect(() => {
    if (state !== "paid" || !user || !info?.campaign_slug || followedRef.current) return;
    followedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/campaigns/${info.campaign_slug}`);
        if (data?.id) await api.post(`/campaigns/${data.id}/follow`).catch(() => {});
      } catch (e) {
        /* best-effort */
      }
    })();
  }, [state, user, info]);

  return (
    <div className="bg-[#F3F4F6]">
      <Container className="flex min-h-[70vh] items-center justify-center py-16">
        <div className="w-full max-w-lg rounded-2xl border border-ff-border bg-white p-10 text-center shadow-sm" data-testid="confirmation-card">
          {state === "polling" && (
            <>
              <Loader2 className="mx-auto mb-5 h-14 w-14 animate-spin text-ff-red" />
              <h1 className="font-heading text-2xl font-bold text-ff-blue">Confirming your donation…</h1>
              <p className="mt-2 text-sm text-ff-dark">Please wait while we verify your payment with Stripe.</p>
            </>
          )}

          {state === "paid" && (
            <div data-testid="confirmation-paid">
              <CheckCircle2 className="mx-auto mb-5 h-16 w-16 text-ff-green" />
              <h1 className="font-heading text-3xl font-extrabold text-ff-blue">Thank you{info?.donor_name && info.donor_name !== "Anonymous" ? `, ${info.donor_name.split(" ")[0]}` : ""}!</h1>
              <p className="mt-3 text-ff-dark">
                Your {info?.amount ? money(info.amount) : ""} donation to <span className="font-semibold">{info?.campaign_title}</span> was successful.
              </p>
              <div className="mt-6 flex items-center justify-center gap-2 rounded-lg bg-[#ECFDF3] p-3 text-sm text-ff-green">
                <Receipt className="h-4 w-4" /> An email receipt is on its way.
              </div>

              {receipt?.branded && (
                <div className="mt-5 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-5 text-left" data-testid="branded-receipt">
                  <div className="flex items-center gap-3">
                    {receipt.brand_logo_url
                      ? <img src={receipt.brand_logo_url} alt="" className="h-10 w-10 rounded-lg object-cover" />
                      : <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FEF3C7] text-[#92400E]"><Crown className="h-5 w-5" /></span>}
                    <div>
                      <div className="font-heading text-sm font-bold text-ff-blue">{receipt.brand_org_name}</div>
                      {receipt.receipt_number && <div className="text-xs text-ff-medium">Receipt {receipt.receipt_number}</div>}
                    </div>
                  </div>
                  {receipt.brand_thank_you && <p className="mt-3 text-sm italic text-ff-dark">"{receipt.brand_thank_you}"</p>}
                  {receipt.brand_tax_line && (
                    <p className="mt-3 text-xs text-ff-medium">
                      This receipt confirms a tax-deductible contribution. No goods or services were provided in exchange.
                      {receipt.brand_ein && <> EIN: {receipt.brand_ein}.</>}
                    </p>
                  )}
                </div>
              )}

              {user ? (
                <div className="mt-8" data-testid="confirmation-push-nudge">
                  <div className="rounded-xl border border-ff-border bg-[#F9FAFB] p-4 text-left">
                    <div className="font-heading text-sm font-bold text-ff-blue">Get updates on this campaign</div>
                    <p className="mt-0.5 text-xs text-ff-medium">
                      You're now following it. Turn on notifications to hear the moment there's news.
                    </p>
                    <PushToggle compact />
                  </div>
                  {info?.campaign_slug && (
                    <Button asChild variant="outline" className="mt-3 w-full border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white">
                      <Link to={`/fundraisers/${info.campaign_slug}`} data-testid="back-to-campaign">Back to campaign</Link>
                    </Button>
                  )}
                </div>
              ) : (
                <div className="mt-8 grid gap-3">
                  <div className="rounded-xl border border-ff-border bg-[#F9FAFB] p-4 text-left" data-testid="confirmation-guest-follow">
                    <div className="font-heading text-sm font-bold text-ff-blue">Follow this campaign &amp; get updates</div>
                    <p className="mt-0.5 text-xs text-ff-medium">
                      Create a free account to follow this cause, get notified when there's news, and give again in one tap.
                    </p>
                  </div>
                  <Button asChild className="bg-ff-blue font-semibold text-white hover:bg-ff-blue/90">
                    <Link to="/donation/create-account" data-testid="create-account-cta">
                      <UserPlus className="mr-2 h-4 w-4" /> Create a free account
                    </Link>
                  </Button>
                  {info?.campaign_slug && (
                    <Button asChild variant="outline" className="border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white">
                      <Link to={`/fundraisers/${info.campaign_slug}`} data-testid="back-to-campaign">Back to campaign</Link>
                    </Button>
                  )}
                </div>
              )}

              <InstallNudge
                headline="Enjoyed donating in seconds?"
                sub="Add FightFund to your home screen to follow this cause and give again in one tap."
                testid="confirmation-install-nudge"
              />
            </div>
          )}

          {state === "pending" && (
            <div data-testid="confirmation-pending">
              <Loader2 className="mx-auto mb-5 h-14 w-14 text-ff-amber" />
              <h1 className="font-heading text-2xl font-bold text-ff-blue">Still processing…</h1>
              <p className="mt-2 text-sm text-ff-dark">
                Your payment is taking a moment to confirm. You'll receive an email receipt once it settles.
              </p>
              <Button asChild variant="outline" className="mt-6 border-ff-blue text-ff-blue">
                <Link to="/browse">Browse more campaigns</Link>
              </Button>
            </div>
          )}

          {state === "error" && (
            <div data-testid="confirmation-error">
              <XCircle className="mx-auto mb-5 h-14 w-14 text-ff-red" />
              <h1 className="font-heading text-2xl font-bold text-ff-blue">We couldn't confirm this donation</h1>
              <p className="mt-2 text-sm text-ff-dark">If you were charged, you'll still receive a receipt. Contact support if you have questions.</p>
              <Button asChild className="mt-6 bg-ff-red font-semibold text-white hover:bg-ff-red/90">
                <Link to="/browse"><Heart className="mr-2 h-4 w-4" /> Browse campaigns</Link>
              </Button>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}
